import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { FareOptionComponent, ClipLoaderCommon, HeaderNavigatorComponent } from "@components";
import {
    breakpoint,
    sessionHelper,
    theme,
    useWindowDimensions,
    type AddToCartProductSnapshot,
} from "@utilities";
import { PATH_CART, PATH_LOGIN, PATH_QUOTES } from "@routes";
import {
    // IProductAvailOption,
    SelectedAvailability,
    IProductPricesDetails,
    IBookingAvailability,
    CheckProductAvailsData,
    CheckProductAvailsDateRange,
    ITourFareBookingTime,
} from "@interfaces";
import { AvailabilityRangePicker, CheckAvailTableModule } from "@module";
import { openToast, useAppSelector, useAppDispatch, selectAuth, selectCheckout } from "@redux";
import { addToCartAndFetch } from "../checkout";
import { DURATION_TIME, SIGN_IN_PROMPT_SEEN_KEY } from "@constants";
import _ from "lodash";
import { LoadingStage } from "src/Interfaces/shared";
import { getProductPricesDetailsId, getBookingAvailabilities } from "src/Utilities/cartHelper";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { AddToCartButton } from "src/Module/TourCheckAvail/AddToCartButton";
import { addToExistingQuoteModal, createNewQuoteModal } from "src/Module/Modals";
import {
    createNewQuoteAndReload,
    loadQuotes,
    addItemToExistingQuoteAndReload,
} from "src/Redux/Slices/allQuotes";

import moment from "moment";
import { SignInBottomSheetModal } from "./SignInBottomSheetModal";
import { shouldPromptSignup } from "../signupPromptGate";
import { stashGuestCartItem } from "../guestCartStash";
import { isDuplicateCartItemError } from "../cartErrors";
import { RedirectToCart } from "./RedirectToCartPopUp";
import { lsJwt } from "src/Utilities/storage";

export const CHANGE_DATE_FROM = "FROM";
export const CHANGE_DATE_TO = "TO";

interface CheckAvailLimitDate {
    from: Date;
    to: Date;
}

interface CheckAvailsProps extends CheckProductAvailsData {
    productClass: string;
    limitDate: CheckAvailLimitDate;
    selectedAvailabilities: SelectedAvailability[];
    setSelectedAvailabilities: (newAvailabilities: SelectedAvailability[]) => void;
    bookingTime?: string;
}

export const ProductAvailabilities = ({
    startDate,
    numDays,
    selectedBookingTimeId,
    productId,
    bookingPriceGroup: productPriceGroup,
    apiProviderId,
    groupFaresForAvailabilityCheck,
    selectedAvailabilities,
    setSelectedAvailabilities,
    bookingTime,
}: CheckAvailsProps) => {
    const dispatch = useAppDispatch();
    const [availabilities, setAvailabilities] = useState<IBookingAvailability[]>([]);
    const [, setAvailLoadingStage] = useState<LoadingStage>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { width } = useWindowDimensions();

    useEffect(() => {
        if (numDays >= 0) {
            setIsLoading(true);
            getBookingAvailabilities({
                startDate,
                numDays,
                selectedBookingTimeId,
                productId,
                bookingPriceGroup: productPriceGroup,
                apiProviderId,
                groupFaresForAvailabilityCheck,
                bookingTime,
            })
                .then((avails) => {
                    setAvailabilities(avails);
                    setAvailLoadingStage(LoadingStage.INITIALIZE_COMPLETE);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setAvailLoadingStage(LoadingStage.LOADING_FAILURE);
                    setIsLoading(false);
                });
        } else {
            dispatch(
                openToast({
                    type: "error",
                    autoHideDuration: DURATION_TIME,
                    message: "Invalid date.",
                }),
            );
        }
    }, [startDate, numDays]);

    const handleAvailCheck = (checkedAvail: SelectedAvailability) => {
        let updatedCheckedDate = [];
        const checkedIndex = selectedAvailabilities.findIndex(
            (date) => checkedAvail.BookingDate === date.BookingDate,
        );

        if (checkedIndex >= 0) {
            updatedCheckedDate = selectedAvailabilities.filter(
                (date) => date.BookingDate !== checkedAvail.BookingDate,
            );
        } else {
            updatedCheckedDate = [...selectedAvailabilities, checkedAvail];
        }

        setSelectedAvailabilities(updatedCheckedDate);
    };

    return (
        <Container>
            {isLoading ? (
                <div className="clip-loader">
                    <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={10} />
                </div>
            ) : availabilities.length < 1 ? (
                <CenteredMessage>
                    There is no availability for this fare. Please change your date or select
                    another fare.
                </CenteredMessage>
            ) : width < 767 ? (
                <>
                    {availabilities.every((availability) => availability.NumAvailable <= 0) ? (
                        <CenteredMessage>No dates available for the selected time.</CenteredMessage>
                    ) : (
                        availabilities
                            .filter((availability) => availability.NumAvailable > 0)
                            .map((availability, index) => {
                                const isChecked = selectedAvailabilities.some(
                                    (dateItem) => dateItem.BookingDate === availability.BookingDate,
                                );
                                return (
                                    <FareOptionComponent
                                        key={`avail-${index}`}
                                        availIndex={index}
                                        isChecked={isChecked}
                                        availability={availability}
                                        onCheck={handleAvailCheck}
                                    />
                                );
                            })
                    )}
                </>
            ) : (
                <CheckAvailTableModule
                    availabilities={availabilities}
                    selectedAvailabilities={selectedAvailabilities}
                    setSelectedAvailabilities={setSelectedAvailabilities}
                    isLoading={isLoading}
                    range={{
                        startDate,
                        numDays,
                    }}
                    productPriceGroup={productPriceGroup}
                    // Cache-related props: passed down to trigger availability caching when user selects a date
                    productId={productId}
                    apiProviderId={apiProviderId}
                    groupFaresForAvailabilityCheck={groupFaresForAvailabilityCheck}
                    timeId={selectedBookingTimeId}
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const CenteredMessage = styled.p`
    text-align: center;
`;

const ModalFooter = styled.div`
    position: sticky;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    padding: 16px;
    border-top: 1px solid #e0e0e0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
`;

interface CheckAvailsModalProps extends CheckProductAvailsData {
    productClass: string;
    /** Minimal product fields for GTM add_to_cart; not passed to availability fetch. */
    addToCartProductSnapshot?: AddToCartProductSnapshot | null;
    limitDate: CheckAvailLimitDate;
    isOpen: boolean;
    onClose: () => void;
    bookingDetails: ITourFareBookingTime;
    bookingTime?: string;
}

const safeParseNumber = (val: any): number => {
    if (_.isNil(val)) return 0;
    return parseFloat(String(val).replace(/[^\d.-]/g, "")) || 0;
};

export const CheckAvailsModal = (props: CheckAvailsModalProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const addToCartRef = useRef<HTMLDivElement | null>(null);
    const { jwt } = useAppSelector(selectAuth);
    const isLoggedIn = useMemo(() => !_.isNil(jwt), [jwt]);
    const productCount = useAppSelector(selectCheckout).cartItems?.length || 0;
    const [, setSearchParams] = useSearchParams();
    const { isOpen, onClose, bookingPriceGroup: productPriceGroup, startDate, numDays } = props;
    const numPax = safeParseNumber(productPriceGroup?.prices?.[0]?.numPax);

    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;
    const [selectedAvailabilities, setSelectedAvailabilities] = useState<SelectedAvailability[]>(
        [],
    );
    const step = numPax || 1;
    const maxQuantity =
        Math.floor(
            (_.min(_.map(selectedAvailabilities, (item) => item.NumAvailable)) || step) / step,
        ) * step;
    const [dateRange, setDateRange] = useState<CheckProductAvailsDateRange>({
        startDate: startDate,
        numDays: Math.abs(numDays),
    });
    /** Raised after a successful Add to Cart, offering the cart or the home page. */
    const [showAddedToCart, setShowAddedToCart] = useState(false);
    const [isSubmitInProgress, setIsSubmitInProgress] = useState<
        null | "cart" | "book-now" | "quote"
    >(null);
    const [quantity, setQuantity] = useState<number>(step);
    const [showSignInPrompt, setShowSignInPrompt] = useState(false);
    const pendingActionRef = useRef<{
        availabilities: SelectedAvailability[];
        mode: "addToCart" | "bookNow" | "newQuote" | "existingQuote";
    } | null>(null);
    const increaseQuantity = () => {
        const allAvailabilitiesAllowIncrease = selectedAvailabilities.every(
            (avail) => avail.NumAvailable >= quantity + step,
        );

        if (!allAvailabilitiesAllowIncrease) {
            ErrorToast({ Message: "Maximum quantity reached for available date" });
            return;
        }

        setQuantity((prev) => (prev + step <= maxQuantity ? prev + step : prev));
    };

    useEffect(() => {
        if (!isOpen) return;

        setSearchParams({
            startdate: moment(dateRange.startDate).format("YYYY-MM-DD"),
            days: dateRange.numDays.toString(),
            bookingTimeId: props.selectedBookingTimeId,
            fareId: productPriceGroup.id,
        });
    }, [startDate, numDays, isOpen]);

    const decreaseQuantity = () => {
        if (quantity - step >= step) {
            setQuantity(quantity - step);
        } else {
            ErrorToast({
                Message: `Minimum quantity is ${step}`,
            });
        }
    };

    const executeAdd = useCallback(
        async (
            availsToAdd: SelectedAvailability[],
            modeToUse: "addToCart" | "bookNow" | "newQuote" | "existingQuote",
        ) => {
            if (!isLoggedIn && (modeToUse === "newQuote" || modeToUse === "existingQuote")) {
                ErrorToast({ Message: "Please login first to create quotes" });
                const returnPath = encodeURIComponent(location.pathname + location.search);
                navigate(`${PATH_LOGIN}?returnPath=${returnPath}`);
                return;
            }
            const actionType =
                modeToUse === "addToCart" ? "cart" : modeToUse === "bookNow" ? "book-now" : "quote";
            try {
                setIsSubmitInProgress(actionType);
                const selectedAvailableIndices = availsToAdd.map((sa) => sa.availIndex);
                const productPricesDetails: IProductPricesDetails[] = _.chain(availsToAdd)
                    .groupBy((sa) => getProductPricesDetailsId(sa))
                    .map((sas, ppdid) => ({
                        productPricesDetailsId: ppdid,
                        quantityDetails: sas.map((sa) => ({
                            quantity: quantity,
                            bookingDate: sa.BookingDate,
                            timeId: props.selectedBookingTimeId,
                        })),
                    }))
                    .value();
                const addItemData = {
                    tdmsProductId: props.productId,
                    productPricesDetails,
                    startDate: moment(dateRange.startDate).format("DD-MMM-YYYY"),
                    days: dateRange.numDays,
                    selectedAvailableIndices,
                };
                if (modeToUse === "newQuote") {
                    try {
                        await createNewQuoteModal().then(async (title?: string) => {
                            if (!_.isEmpty(title)) {
                                await dispatch(
                                    createNewQuoteAndReload({
                                        ...addItemData,
                                        quoteTitle: title!,
                                    }),
                                ).unwrap();
                                navigate(PATH_QUOTES);
                            }
                        });
                    } catch (error: any) {
                        ErrorToast({ Message: error.title });
                    }
                } else if (modeToUse === "existingQuote") {
                    try {
                        await dispatch(loadQuotes());
                        const quoteId = await addToExistingQuoteModal();

                        if (!quoteId) {
                            ErrorToast({ Message: "No quote selected." });
                            return;
                        }

                        await dispatch(
                            addItemToExistingQuoteAndReload({
                                ...addItemData,
                                quoteId: quoteId.id,
                            }),
                        ).unwrap();

                        navigate(PATH_QUOTES);
                    } catch (error: any) {
                        console.error("Error adding to existing quote:", error);
                        ErrorToast({ Message: error.title });
                    }
                } else if (modeToUse === "bookNow") {
                    // See AllFaresAvailabilityPanel: isLoggedIn is stale when the
                    // sign-in sheet replays this in the same tick as storing the JWT.
                    const signedIn = isLoggedIn || !_.isNil(lsJwt.getItem()?.accessToken);
                    // Book Now checks out everything waiting, not just this
                    // product: it adds to the same session Add to Cart uses and
                    // opens the cart's Checkout step, so one order covers the lot.
                    // Previously it ran on its own session that was wiped on every
                    // click, which is why anything already in the cart vanished.
                    const sessionId = signedIn ? undefined : sessionHelper.getSessionId();
                    try {
                        await dispatch(
                            addToCartAndFetch({ item: addItemData, sessionId }),
                        ).unwrap();
                    } catch (addError: unknown) {
                        // Book Now on something already in the cart is not a
                        // failure: the intent is to check this product out and it
                        // is already there. Anything else still surfaces.
                        if (!isDuplicateCartItemError(addError)) throw addError;
                    }
                    navigate(
                        sessionId
                            ? `${PATH_CART}?sessionId=${sessionId}&step=1`
                            : `${PATH_CART}?step=1`,
                    );
                } else if (modeToUse === "addToCart") {
                    const signedIn = isLoggedIn || !_.isNil(lsJwt.getItem()?.accessToken);
                    const sessionId = signedIn ? undefined : sessionHelper.getSessionId();
                    await dispatch(addToCartAndFetch({ item: addItemData, sessionId })).unwrap();
                    // Kept so the sign-up can put it on the new account if the
                    // backend's session convert drops it (see guestCartStash).
                    if (!signedIn) stashGuestCartItem(addItemData);
                    // Asked here rather than after a hop to the home page: being asked
                    // "view your cart?" on a page you did not choose to be on has
                    // already answered the question for you.
                    setShowAddedToCart(true);
                }
            } catch (error: any) {
                ErrorToast({ Message: error || "Failed to add item" });
            } finally {
                setIsSubmitInProgress(null);
            }
        },
        [isLoggedIn, quantity, numPax, props, dateRange, dispatch, navigate, location],
    );

    const handleAdd = async (
        selectedAvailabilities: SelectedAvailability[],
        mode: "addToCart" | "bookNow" | "newQuote" | "existingQuote",
    ) => {
        if (quantity < step || quantity % step !== 0) {
            ErrorToast({ Message: `Quantity must be a multiple of ${step}` });
            return;
        }
        const overAvailability = selectedAvailabilities.find((a) => quantity > a.NumAvailable);
        if (overAvailability) {
            ErrorToast({ Message: "Quantity exceeds available spaces" });
            return;
        }
        if (
            shouldPromptSignup(mode === "bookNow" ? "bookNow" : "addToCart", {
                isLoggedIn,
                promptSeen: !!sessionStorage.getItem(SIGN_IN_PROMPT_SEEN_KEY),
            })
        ) {
            pendingActionRef.current = { availabilities: selectedAvailabilities, mode };
            setShowSignInPrompt(true);
            return;
        }
        await executeAdd(selectedAvailabilities, mode);
    };

    const handleSignInPromptClose = () => {
        setShowSignInPrompt(false);
        pendingActionRef.current = null;
    };

    const handleSignInSuccess = () => {
        sessionStorage.setItem(SIGN_IN_PROMPT_SEEN_KEY, "true");
        setShowSignInPrompt(false);
        const pending = pendingActionRef.current;
        pendingActionRef.current = null;
        if (pending) {
            executeAdd(pending.availabilities, pending.mode);
        }
    };

    return (
        <>
            <ModalOverlay onClick={handleClose}>
                <FixedHeaderContainer>
                    <HeaderNavigatorComponent
                        $onBackToHome={handleClose}
                        $productCount={productCount}
                    />
                </FixedHeaderContainer>
                <ModalContainer onClick={(e) => e.stopPropagation()}>
                    <ModalHeader>
                        <CloseButton onClick={handleClose}>
                            <img src="/images/fi_x.png" alt="close" />
                        </CloseButton>
                        <div className="price-group-info">
                            <h2 className="text">{productPriceGroup.name}</h2>
                            {productPriceGroup.travelTimeMessage && (
                                <div className="travel-time">
                                    {productPriceGroup.travelTimeMessage}
                                </div>
                            )}
                        </div>
                        <DateField>
                            <AvailabilityRangePicker
                                range={dateRange}
                                limitDate={props.limitDate}
                                onRangeChange={(startDate, numDays) => {
                                    setDateRange({
                                        startDate,
                                        numDays,
                                    });
                                }}
                            />
                        </DateField>
                    </ModalHeader>
                    <ModalBody>
                        <ProductAvailabilities
                            {...props}
                            startDate={dateRange.startDate}
                            numDays={dateRange.numDays}
                            selectedAvailabilities={selectedAvailabilities}
                            setSelectedAvailabilities={setSelectedAvailabilities}
                            bookingTime={props.bookingTime}
                        />
                    </ModalBody>
                    {selectedAvailabilities.length > 0 && (
                        <ModalFooter>
                            <AddToCartButton
                                ref={addToCartRef}
                                productClass={props.productClass}
                                addToCartProductSnapshot={props.addToCartProductSnapshot}
                                isSubmitInProgress={isSubmitInProgress}
                                selectedAvailabilities={selectedAvailabilities}
                                selectedBookingTimeId={props.selectedBookingTimeId}
                                bookingDetails={props.bookingDetails}
                                bookingSystem={props.bookingPriceGroup.bookingSystem}
                                productPriceGroup={props.bookingPriceGroup}
                                handleAdd={handleAdd}
                                increaseQuantity={increaseQuantity}
                                decreaseQuantity={decreaseQuantity}
                                quantity={quantity}
                            />
                        </ModalFooter>
                    )}
                </ModalContainer>
            </ModalOverlay>
            <RedirectToCart
                open={showAddedToCart}
                onClose={() => setShowAddedToCart(false)}
                cartUrl={
                    isLoggedIn
                        ? PATH_CART
                        : `${PATH_CART}?sessionId=${sessionHelper.getSessionId()}`
                }
            />
            {showSignInPrompt && (
                <SignInBottomSheetModal
                    isOpen={showSignInPrompt}
                    onClose={handleSignInPromptClose}
                    sessionId={sessionHelper.getSessionId()}
                    onLoginSuccess={handleSignInSuccess}
                />
            )}
        </>
    );
};

const FixedHeaderContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1001;
    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.15);
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    -webkit-box-align: center;
    -moz-box-align: center;

    @media (max-width: 767px) {
        padding-top: 0;
        align-items: flex-start;
        background-color: #fff;
        overflow-y: auto;
    }

    color: #333;
    font-family: "Poppins", sans-serif;
    font-size: 15px;
`;

const ModalContainer = styled.div`
    background: #fff;
    border-radius: 8px;
    width: 95%;
    max-width: 1200px;
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    @media (max-width: 767px) {
        padding-top: 80px;
        width: 100%;
        height: 100vh;
        max-height: 100%;
        border-radius: 0;
        overflow-y: auto;
    }
`;

const ModalHeader = styled.div`
    position: relative;
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    z-index: 1000;

    .price-group-info {
        flex: 1;
        margin-right: 20px;
        text-align: left;

        .text {
            margin: 15px 0;
        }

        .travel-time {
            margin-top: 8px;
        }
    }

    @media (max-width: 767px) {
        flex-direction: column;
        background-color: #f8f8f8;

        .price-group-info {
            width: 100%;
            margin-right: 0;
            margin-bottom: 16px;
        }
    }
`;

const DateField = styled.div`
    display: flex;
    gap: 20px;
    flex-shrink: 0;
    margin-right: 20px;
    width: 300px;

    @media (max-width: 767px) {
        margin-right: 0px;
        width: 100%;
    }

    .datePicker {
        .MuiInputLabel-animated {
            color: #fff;
        }

        .MuiFormControl-marginNormal {
            margin: 0;
        }

        .MuiInputBase-root {
            background-color: ${(p) => p.theme.colors.gray_1} !important;
            height: 36px;
            width: 135px;

            ${breakpoint.breakIpadPro`
            width: 160px;
        `}

            ${breakpoint.breakLarge`
            width: 214px;
        `}
        }

        .MuiInputBase-input {
            color: #fff;
        }
    }
`;

const ModalBody = styled.div`
    padding: 20px;
    overflow-y: auto;
    flex: 1;
`;
const CloseButton = styled.button`
    position: absolute;
    right: 14px;
    top: 22px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    &:hover {
        color: #000;
    }
    z-index: 1001;
`;
